import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';
import lodashSet from 'lodash.set';
import { resetQuestionnaire } from '../../../actions';

type TYesOrNo = 'yes' | 'no' | null;

export type TDependentsChildren = TDependentsChildrenRow[];

export type TDependentsChildrenRow = {
  firstName: Nullable<string>;
  lastName: Nullable<string>;
  dateOfBirth: Nullable<string>;
  relationship: Nullable<string>;
  SSN: Nullable<string>;
};
export interface DependentsStateValues {
  dependentsChildren: TDependentsChildren;
  documentShowSameAddress: Nullable<string>;
  provideHouseholdExpenses: TYesOrNo;
  whoClaimDependents: Nullable<string>;
}

export interface DependentsState {
  values: DependentsStateValues;
}

const initialState: DependentsState = {
  values: {
    dependentsChildren: [
      {
        relationship: null,
        firstName: null,
        lastName: null,
        dateOfBirth: null,
        SSN: null,
      },
    ],
    documentShowSameAddress: null,
    provideHouseholdExpenses: null,
    whoClaimDependents: null,
  },
};

const dependentsSlice = createSlice({
  name: 'dependents',
  initialState,
  reducers: {
    update(state, action: PayloadAction<DependentsStateValues>) {
      state.values = JSON.parse(JSON.stringify(action.payload));
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof DependentsStateValues | keyof TDependentsChildrenRow;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },
    updateFieldArrayKey(
      state,
      action: PayloadAction<{
        key: keyof DependentsStateValues | keyof TDependentsChildrenRow;
        value: TDependentsChildren;
      }>,
    ) {
      const fieldArray = action.payload.value.map((field) => ({ ...field }));
      if (action.payload.key === 'dependentsChildren') {
        state.values[action.payload.key] = fieldArray;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetQuestionnaire, () => initialState);

    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        if (payload.questionnaire.data.taxQuestionnaire) {
          const dependentsChildren =
            payload.questionnaire.data.taxQuestionnaire.personal_details
              .the_basics.dependents.values.dependentsChildren;
          if (dependentsChildren.length === 0) {
            state.values = {
              ...payload.questionnaire.data.taxQuestionnaire.personal_details
                .the_basics.dependents.values,
              dependentsChildren: [],
            };
          } else {
            state.values =
              payload.questionnaire.data.taxQuestionnaire.personal_details.the_basics.dependents.values;
          }
        } else {
          state.values = initialState.values;
        }
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } =
  dependentsSlice.actions;
export default dependentsSlice.reducer;

export const selectDependents = (state: RootState) =>
  state.taxQuestionnaire.personal_details.the_basics.dependents;
